// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type TierStatus = "active" | "disabled";

export interface ServiceTier {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: TierStatus;
  readonly maxProbesPerTenant: number;
  readonly maxUsersPerTenant: number;
  readonly maxNotificationContactsPerTenant: number;
  readonly minProbeRequestDelayMs: number;
  readonly minProbeUploadIntervalMs: number;
  readonly fullLogRetentionDays: number;
  readonly maxAggregatedLogRetentionDays: number;
  readonly tenantCount: number;
}

export interface SaveTierInput {
  readonly name: string;
  readonly description?: string | null;
  readonly status: TierStatus;
  readonly maxProbesPerTenant: number;
  readonly maxUsersPerTenant: number;
  readonly maxNotificationContactsPerTenant: number;
  readonly minProbeRequestDelayMs: number;
  readonly minProbeUploadIntervalMs: number;
  readonly fullLogRetentionDays: number;
  readonly maxAggregatedLogRetentionDays: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Tier response field ${fieldName} is invalid`);
  }
  return value;
}

function readInteger(value: unknown, fieldName: string): number {
  if (!Number.isInteger(value)) {
    throw new Error(`Tier response field ${fieldName} is invalid`);
  }
  return value as number;
}

function readTier(payload: unknown, fieldName: string): ServiceTier {
  if (!isRecord(payload)) {
    throw new Error(`Tier response field ${fieldName} is invalid`);
  }
  const status: string = readString(payload.status, `${fieldName}.status`);
  if (status !== "active" && status !== "disabled") {
    throw new Error(`Tier response field ${fieldName}.status is invalid`);
  }
  if (payload.description !== null && typeof payload.description !== "string") {
    throw new Error(`Tier response field ${fieldName}.description is invalid`);
  }

  return {
    id: readString(payload.id, `${fieldName}.id`),
    name: readString(payload.name, `${fieldName}.name`),
    description: payload.description,
    status,
    maxProbesPerTenant: readInteger(payload.maxProbesPerTenant, `${fieldName}.maxProbesPerTenant`),
    maxUsersPerTenant: readInteger(payload.maxUsersPerTenant, `${fieldName}.maxUsersPerTenant`),
    maxNotificationContactsPerTenant: readInteger(payload.maxNotificationContactsPerTenant, `${fieldName}.maxNotificationContactsPerTenant`),
    minProbeRequestDelayMs: readInteger(payload.minProbeRequestDelayMs, `${fieldName}.minProbeRequestDelayMs`),
    minProbeUploadIntervalMs: readInteger(payload.minProbeUploadIntervalMs, `${fieldName}.minProbeUploadIntervalMs`),
    fullLogRetentionDays: readInteger(payload.fullLogRetentionDays, `${fieldName}.fullLogRetentionDays`),
    maxAggregatedLogRetentionDays: readInteger(payload.maxAggregatedLogRetentionDays, `${fieldName}.maxAggregatedLogRetentionDays`),
    tenantCount: readInteger(payload.tenantCount, `${fieldName}.tenantCount`),
  };
}

export async function listTiers(): Promise<readonly ServiceTier[]> {
  const payload: unknown = await requestJson("/api/v1/system/tiers");
  if (!isRecord(payload) || !Array.isArray(payload.tiers)) {
    throw new Error("Tier list response is invalid");
  }
  return payload.tiers.map((tier: unknown, index: number): ServiceTier =>
    readTier(tier, `tiers.${index}`),
  );
}

export async function createTier(input: SaveTierInput): Promise<ServiceTier> {
  return readTier(
    await requestJson("/api/v1/system/tiers", { method: "POST", body: JSON.stringify(input) }),
    "tier",
  );
}

export async function updateTier(tierId: string, input: SaveTierInput): Promise<ServiceTier> {
  return readTier(
    await requestJson(`/api/v1/system/tiers/${encodeURIComponent(tierId)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
    "tier",
  );
}

export async function deleteTier(tierId: string): Promise<void> {
  await requestJson(`/api/v1/system/tiers/${encodeURIComponent(tierId)}`, { method: "DELETE" });
}
